import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HorizontalRule } from "@ucs/Components/HorizontalRule";
import { Roles } from "@ucs/Helpers/Enums";
import { formatDateByYearMonthDay } from "@ucs/Helpers/Formatting";
import ChevronRight from "@ucs/Icons/ChevronRight";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        isAccounting: state.context.session!.userRoles ? state.context.session!.userRoles.includes(Roles.Accounting) : false,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    viewAllButton: any;
    containerName: any;
    topContainerTile: any;
    topHeadingContainer: any;
    dataTable: any;
    listNameLink: any;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    listNameLink: {
        css: css`
            color: ${UCSColors.green00};
            white-space: no-wrap;
        `,
    },
    noQuotesGridItem: { width: 12 },
    topHeadingContainer: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            width: 100%;
            margin-bottom: 8px !important;
        `,
    },
    topContainerTile: {
        css: css`
            background: #ffffff;
            padding: 10px;
            padding-left: 20px;
            padding-right: 20px;
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            margin-bottom: 8px;
        `,
    },
    containerName: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-start;
        `,
    },
    viewAllButton: {
        css: css`
            display: flex;
            flex: 1;
            justify-content: flex-end;
        `,
    },
    dataTable: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: ${maxWidths[1]}px;
                        #listId {
                            order: 2 !important;
                        }
                        #previewId {
                            order: 1 !important;
                        }
                        #updatedId {
                            order: 3 !important;
                        }
                        #sharingId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[2]}px;
                        #listId {
                            order: 2 !important;
                        }
                        #previewId {
                            order: 1 !important;
                        }
                        #updatedId {
                            order: 3 !important;
                        }
                        #sharingId {
                            order: 4 !important;
                        }
                    `,
                    css`
                        max-width: ${maxWidths[3]}px;
                    `,
                    css`
                        max-width: ${maxWidths[4]}px;
                    `,
                    css`
                        max-width: ${maxWidths[5]}px;
                    `,
                ]);
            }}
        `,
        cell: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: center;
                color: ${UCSColors.gray03};
            `,
        },
        header: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: column;
                align-self: auto;
                justify-self: auto;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        headerContainer: {
            css: css`
                display: flex;
                flex-direction: row;
                width: 100%;
                gap: 40px;
            `,
        },
        row: {
            css: css`
                display: flex;
                flex: 1;
                flex-direction: row;
                gap: 40px;
                padding-top: 2px;
                padding-bottom: 2px;
            `,
        },
        previewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
                overflow: auto;
                p {
                    overflow-y: hidden;
                }
            `,
        },
        mobilePreviewImages: {
            css: css`
                display: flex;
                flex-direction: row;
                flex: 1;
                gap: 10px;
                overflow: auto;
                p {
                    overflow-y: hidden;
                }
            `,
        },
        mobileCell: {
            css: css`
                display: flex;
                flex-direction: column;
                white-space: no-wrap;
                color: ${UCSColors.gray03};
            `,
        },
        mobileHeader: {
            css: css`
                display: flex;
                flex-direction: column;
                align-self: center;
                justify-content: flex-start;
                text-transform: uppercase;
                font-weight: bold;
                color: ${UCSColors.gray04};
            `,
        },
        mobileHeaderContainer: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },

        mobileRow: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-self: start;
            `,
        },
        innerTwoGrid: {
            css: css`
                display: grid;
                grid-template-columns: [first] 86px [line2] auto;
                grid-auto-flow: column;
                align-items: start;
            `,
        },
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
    sort: "orderDate DESC",
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, rfqQuoteDetailsPageUrl, canViewQuotes, isAccounting } = this.props;
        if (!canViewQuotes || !quotesDataView.value || isAccounting) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            <>
                <StyledWrapper {...styles.topContainerTile}>
                    <StyledWrapper {...styles.topHeadingContainer}>
                        <Typography size={28} {...styles.containerName}>
                            {translate("Recent Quotes")}
                        </Typography>
                        <Link
                            href={myQuotesPageUrl}
                            target="_self"
                            icon={{
                                iconProps: {
                                    color: `${UCSColors.green00}`,
                                    size: 16,
                                    src: ChevronRight,
                                },
                            }}
                        >
                            <Typography size={18} {...styles.viewAllButton}>
                                {translate("View all ")}
                            </Typography>
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper role="presentation" {...styles.dataTable}>
                        <Hidden below="md">
                            <HorizontalRule />
                            <StyledWrapper {...styles.dataTable.headerContainer}>
                                <StyledWrapper id="quoteId" {...styles.dataTable.header}>
                                    <Typography size={13}>{translate("Quote #")}</Typography>
                                </StyledWrapper>
                                <StyledWrapper id="dateSubmittedId" {...styles.dataTable.header}>
                                    <Typography size={13}>{translate("Date Submitted")}</Typography>
                                </StyledWrapper>
                                {/* 
                                UFG-303
                                <StyledWrapper id="dateRequestedId" {...styles.dataTable.header}>
                                    <Typography size={13}>{translate("Date Requested")}</Typography>
                                </StyledWrapper> 
                                UFG-303
                                */}
                                <StyledWrapper id="dateRequestedId" {...styles.dataTable.header}>
                                    <Typography size={13}>{translate("Requested Ship Date")}</Typography>
                                </StyledWrapper>
                                <StyledWrapper id="statusId" {...styles.dataTable.header}>
                                    <Typography size={13}>{translate("Status")}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            <HorizontalRule />
                        </Hidden>
                        <Hidden above="sm">
                            <HorizontalRule />
                        </Hidden>
                        {quotesDataView.value.length === 0 && (
                            <GridItem {...styles.noQuotesGridItem}>
                                <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                            </GridItem>
                        )}
                        {quotesDataView.value.map(quote => (
                            <StyledWrapper key={quote.id}>
                                <StyledWrapper role="row" {...styles.dataTable.row}>
                                    <Hidden below="md" {...styles.dataTable.row}>
                                        <StyledWrapper id="quoteId" {...styles.dataTable.cell}>
                                            <Link
                                                href={`${rfqQuoteDetailsPageUrl}?quoteId=${quote.id}`}
                                                target="_self"
                                            >
                                                <Typography {...styles.listNameLink} size={18}>
                                                    {quote.quoteNumber}
                                                </Typography>
                                            </Link>
                                        </StyledWrapper>
                                        <StyledWrapper id="dateSubmittedId" {...styles.dataTable.cell}>
                                            <Typography size={18}>
                                                {quote.orderDate ? formatDateByYearMonthDay(quote.orderDate) : ""}
                                            </Typography>
                                        </StyledWrapper>
                                        {/* UFG-303
                                        <StyledWrapper id="dateRequestedId" {...styles.dataTable.cell}>
                                            <Typography size={18}>
                                                {quote.requestedPickupDateDisplay
                                                    ? formatDateByYearMonthDay(quote.requestedPickupDateDisplay)
                                                    : ""}
                                                {quote.requestedDeliveryDateDisplay !== null
                                                    ? formatDateByYearMonthDay(quote.requestedDeliveryDateDisplay)
                                                    : ""}
                                            </Typography>
                                        </StyledWrapper> 
                                        UFG 303
                                        */}
                                        <StyledWrapper id="dateSubmittedId" {...styles.dataTable.cell}>
                                            <Typography size={18}>
                                                {quote.properties.requestedShipDateForQuote ? formatDateByYearMonthDay(new Date(quote.properties.requestedShipDateForQuote.replace(/-/g, "/"))) : ""}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper id="statusId" {...styles.dataTable.cell}>
                                            <Typography size={18}>{quote.status}</Typography>
                                        </StyledWrapper>
                                    </Hidden>
                                    <Hidden above="sm" {...styles.dataTable.mobileRow}>
                                        <StyledWrapper {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Requested Ship Date")}
                                                </Typography>
                                                <Typography size={14}>{quote.properties.requestedShipDateForQuote ? formatDateByYearMonthDay(new Date(quote.properties.requestedShipDateForQuote.replace(/-/g, "/"))) : ""}</Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper id="quoteId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Quote #")}
                                                </Typography>
                                                <Link
                                                    href={`${rfqQuoteDetailsPageUrl}?quoteId=${quote.id}`}
                                                    target="_self"
                                                >
                                                    <Typography {...styles.listNameLink} size={14}>
                                                        {quote.quoteNumber}
                                                    </Typography>
                                                </Link>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper id="dateSubmittedId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Date Submitted")}
                                                </Typography>
                                                <Typography size={14}>{quote.orderDate ? formatDateByYearMonthDay(quote.orderDate) : ""}</Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        {/* UFG-303
                                         <StyledWrapper id="dateRequestedId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Date Requested")}
                                                </Typography>
                                                <Typography size={14}>
                                                    {quote.requestedDeliveryDateDisplay}
                                                    {this.formatDate(
                                                        quote.requestedDeliveryDateDisplay ||
                                                            quote.requestedPickupDateDisplay,
                                                    )}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        UFG-303
                                        */}
                                        <StyledWrapper id="statusId" {...styles.dataTable.mobileCell}>
                                            <StyledWrapper {...styles.dataTable.innerTwoGrid}>
                                                <Typography {...styles.dataTable.mobileHeader} size={10}>
                                                    {translate("Status")}
                                                </Typography>
                                                <Typography size={14}>{quote.status}</Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </Hidden>
                                </StyledWrapper>
                                <HorizontalRule />
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "My Account",
        icon: "List",
    },
};

export default widgetModule;
